import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/functions';

export interface StudyShare {
  id: string;
  study_instance_uid: string;
  case_name: string;
  shared_by_user_id: string;
  shared_by_facility_id: string;
  shared_to_facility_id: string;
  shared_at: firebase.firestore.Timestamp;
  base_expires_at: firebase.firestore.Timestamp;
  effective_expires_at: firebase.firestore.Timestamp;
  spoke_timezone: string;
  status: 'active' | 'expiring_soon' | 'expired' | 'deleted' | 'deletion_failed' | 're_requested' | 'reshared' | 'rejected' | 'uploaded_to_pacs';
  expired_at?: firebase.firestore.Timestamp;
  uploaded_to_pacs_at?: firebase.firestore.Timestamp;
  deleted_at?: firebase.firestore.Timestamp;
  rejected_at?: firebase.firestore.Timestamp;
  deletion_retry_count?: number;
  deletion_last_error?: string;
  deletion_note?: string;
  last_accessed_at?: firebase.firestore.Timestamp;
  access_count: number;
  re_request_count: number;
  re_request_sent_at?: firebase.firestore.Timestamp;
  reshared_share_id?: string;
  notification_sent_24h: boolean;
  notification_sent_4h: boolean;
  // DICOM metadata captured at send time (unavailable after Orthanc deletion)
  patient_name?: string;
  patient_id?: string;
  study_date?: string;
  accession_number?: string;
  num_series?: number;
  num_instances?: number;
}

export type StudyShareDisplayStatus = 'active' | 'expiring_soon' | 'expired';

/** Derives the display status from the current time vs effective_expires_at. */
export function getShareDisplayStatus(share: StudyShare): StudyShareDisplayStatus {
  if (!share?.effective_expires_at) return 'active';
  const now = Date.now();
  const expires = share.effective_expires_at.toDate().getTime();
  if (now >= expires) return 'expired';
  if (expires - now <= 4 * 3600 * 1000) return 'expiring_soon';
  return 'active';
}

@Injectable({ providedIn: 'root' })
export class StudyShareService {
  private collection() {
    return firebase.firestore().collection('study_shares');
  }

  /**
   * Real-time stream of all share records sent to a Spoke facility.
   * Returns all statuses so the UI can show available studies alongside history.
   */
  watchSharesForFacility(facilityId: string): Observable<StudyShare[]> {
    return new Observable(observer => {
      const unsub = this.collection()
        .where('shared_to_facility_id', '==', facilityId)
        .where('status', 'in', ['active', 'expiring_soon', 'expired', 'deleted', 'deletion_failed', 're_requested', 'reshared', 'rejected', 'uploaded_to_pacs'])
        .orderBy('effective_expires_at', 'desc')
        .limit(200)
        .onSnapshot(
          snap => observer.next(snap.docs.map(d => ({ id: d.id, ...d.data() } as StudyShare))),
          err => observer.error(err)
        );
      return unsub;
    });
  }

  /**
   * Real-time stream of all shares sent by a Hub facility (Hub dashboard).
   * Returns all statuses so Hub can see the full lifecycle of every send.
   */
  watchSharesSentByFacility(hubFacilityId: string): Observable<StudyShare[]> {
    return new Observable(observer => {
      const unsub = this.collection()
        .where('shared_by_facility_id', '==', hubFacilityId)
        .orderBy('effective_expires_at', 'desc')
        .limit(200)
        .onSnapshot(
          snap => observer.next(snap.docs.map(d => ({ id: d.id, ...d.data() } as StudyShare))),
          err => observer.error(err)
        );
      return unsub;
    });
  }

  /**
   * Real-time stream of re-share requests pending Hub approval.
   * Used by Hub admins to see what Spoke facilities are requesting.
   */
  watchPendingReshareRequests(hubFacilityId: string): Observable<StudyShare[]> {
    return new Observable(observer => {
      const unsub = this.collection()
        .where('shared_by_facility_id', '==', hubFacilityId)
        .where('status', '==', 're_requested')
        .orderBy('re_request_sent_at', 'desc')
        .limit(50)
        .onSnapshot(
          snap => observer.next(snap.docs.map(d => ({ id: d.id, ...d.data() } as StudyShare))),
          err => observer.error(err)
        );
      return unsub;
    });
  }

  /** Spoke: submit a re-share request for an expired share. */
  requestReshare(shareId: string): Promise<{ success: boolean; message: string }> {
    const fn = firebase.functions().httpsCallable('studyLifetime-requestReshare');
    return fn({ shareId }).then(r => r.data);
  }

  /** Hub: approve a pending re-share request. */
  approveReshare(shareId: string): Promise<{ success: boolean }> {
    const fn = firebase.functions().httpsCallable('studyLifetime-approveReshare');
    return fn({ shareId }).then(r => r.data);
  }

  /** Hub: reject a pending re-share request. */
  rejectReshare(shareId: string): Promise<{ success: boolean }> {
    const fn = firebase.functions().httpsCallable('studyLifetime-rejectReshare');
    return fn({ shareId }).then(r => r.data);
  }

  /** Spoke: confirm a study was ingested into their PACS. */
  markUploadedToPacs(shareId: string): Promise<{ success: boolean }> {
    const fn = firebase.functions().httpsCallable('studyLifetime-markUploadedToPacs');
    return fn({ shareId }).then(r => r.data);
  }

  /**
   * Real-time stream of shares sent by Hub to a specific Spoke facility.
   * Two equality conditions, no orderBy — no composite index required.
   * Used by Hub SEND mode to show per-study last-shared status.
   */
  watchSharesForFacilityPair(hubFacilityId: string, spokeFacilityId: string): Observable<StudyShare[]> {
    return new Observable(observer => {
      const unsub = this.collection()
        .where('shared_by_facility_id', '==', hubFacilityId.toLowerCase())
        .where('shared_to_facility_id', '==', spokeFacilityId.toLowerCase())
        .limit(500)
        .onSnapshot(
          snap => observer.next(snap.docs.map(d => ({ id: d.id, ...d.data() } as StudyShare))),
          err => observer.error(err)
        );
      return unsub;
    });
  }

  /**
   * One-time fetch of all shares sent for a specific patient by the Hub facility.
   * No composite index needed — two equality conditions, no orderBy.
   */
  getSharesForPatient(caseName: string, hubFacilityId: string): Promise<StudyShare[]> {
    return this.collection()
      .where('shared_by_facility_id', '==', hubFacilityId.toLowerCase())
      .where('case_name', '==', caseName.toLowerCase())
      .get()
      .then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() } as StudyShare)));
  }
}
